import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useNotifications } from "@/lib/notifications";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Bell, CheckCircle, Envelope, EnvelopeOpen, Trash, ArrowRight, DesktopTower } from "@phosphor-icons/react";

const TYPE_LABELS = {
  user_registration: "Nuova registrazione",
  practice_message: "Messaggio pratica",
  practice_assigned: "Pratica assegnata",
  practice_rejected: "Pratica rifiutata",
  practice_completed: "Pratica completata",
  compensation_paid: "Compenso pagato",
  chatbot_escalation: "Chatbot escalation",
  chatbot_contact_request: "Richiesta contatto chatbot",
  availability_reminder: "Promemoria disponibilità",
};

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "adesso";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} g fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
};

export default function Notifications() {
  const nav = useNavigate();
  const { refresh, requestDesktopPermission } = useNotifications();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all"); // all | unread | read
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [desktopPerm, setDesktopPerm] = useState(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
  );

  const askDesktop = async () => {
    const r = await requestDesktopPermission();
    setDesktopPerm(r.permission);
    if (r.permission === "granted") toast.success("Notifiche desktop attive");
    else if (r.permission === "denied") toast.error("Notifiche desktop bloccate dal browser. Sbloccale dalle impostazioni del sito.");
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter === "unread") params.only_unread = true;
      if (typeFilter !== "all") params.type = typeFilter;
      const [r, tr] = await Promise.all([
        api.get("/notifications", { params }),
        api.get("/notifications/types"),
      ]);
      let list = r.data;
      if (statusFilter === "read") list = list.filter(n => n.is_read);
      setItems(list);
      setTypes(tr.data);
      setSelected(new Set());
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, typeFilter]);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i._id)));
  };

  const bulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      if (action === "read") await api.post("/notifications/mark-read", { ids });
      else if (action === "unread") await api.post("/notifications/mark-unread", { ids });
      else if (action === "delete") {
        if (!window.confirm(`Eliminare ${ids.length} notifiche selezionate? Operazione irreversibile.`)) return;
        await api.post("/notifications/delete", { ids });
      }
      toast.success(`${ids.length} notifich${ids.length === 1 ? "a aggiornata" : "e aggiornate"}`);
      setSelected(new Set());
      await load();
      refresh();
    } catch (e) {
      toast.error("Errore operazione");
    }
  };

  const markAllRead = async () => {
    await api.post("/notifications/mark-read", { all: true });
    toast.success("Tutte segnate come lette");
    await load();
    refresh();
  };

  const onRowClick = async (n) => {
    if (!n.is_read) {
      await api.post("/notifications/mark-read", { ids: [n._id] });
      refresh();
    }
    if (n.target_url && n.target_url !== "/" && n.target_url.startsWith("/")) nav(n.target_url);
  };

  const counts = useMemo(() => {
    return {
      total: items.length,
      unread: items.filter(i => !i.is_read).length,
    };
  }, [items]);

  const typeLabel = (t) => TYPE_LABELS[t] || t;

  return (
    <div data-testid="notifications-page">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Bacheca</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2 flex items-center gap-3">
            <Bell size={30} weight="duotone" className="text-purple-700" /> Notifiche
          </h1>
          <p className="text-muted-foreground mt-2">Triage centrale · filtra, seleziona e marca in batch</p>
        </div>
        <div className="flex items-start gap-2 flex-wrap justify-end">
        {counts.unread > 0 && (
          <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read-btn">
            <CheckCircle size={16} className="mr-2" weight="bold" /> Segna tutte come lette
          </Button>
        )}
        {desktopPerm === "default" && (
          <Button variant="outline" onClick={askDesktop} data-testid="ask-desktop-btn"
            className="border-purple-300 text-purple-800 hover:bg-purple-50">
            <DesktopTower size={16} className="mr-2" weight="duotone" /> Attiva notifiche desktop
          </Button>
        )}
        {desktopPerm === "granted" && (
          <div className="text-xs text-green-700 flex items-center gap-2 px-3 py-2 rounded-md bg-green-50 border border-green-200">
            <DesktopTower size={16} weight="duotone" /> Notifiche desktop attive
          </div>
        )}
        {desktopPerm === "denied" && (
          <div className="text-xs text-amber-700 flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200">
            <DesktopTower size={16} weight="duotone" /> Notifiche desktop bloccate — abilitale dalle impostazioni del sito nel browser
          </div>
        )}
        </div>
      </div>

      {/* Filters */}
      <div className="kpi-card mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 border rounded-md p-1 bg-white">
            {[
              { k: "all", label: "Tutte" },
              { k: "unread", label: `Non lette${counts.unread ? ` (${counts.unread})` : ""}` },
              { k: "read", label: "Lette" },
            ].map(f => (
              <button
                key={f.k}
                onClick={() => setStatusFilter(f.k)}
                className={`px-3 py-1.5 text-xs rounded ${statusFilter === f.k ? "ht-gradient text-white" : "hover:bg-slate-100"}`}
                data-testid={`filter-status-${f.k}`}
              >{f.label}</button>
            ))}
          </div>
          <select
            className="h-9 border rounded-md px-3 text-sm bg-white"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            data-testid="filter-type"
          >
            <option value="all">Tutti i tipi</option>
            {types.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
          </select>
          <div className="flex-1" />
          <div className="text-xs text-muted-foreground">
            {counts.total} risultati · {counts.unread} da leggere
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="kpi-card mb-3 bg-purple-50 border-purple-300 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <strong>{selected.size}</strong> selezionat{selected.size === 1 ? "a" : "e"}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => bulk("read")} data-testid="bulk-read">
              <EnvelopeOpen size={14} className="mr-2" /> Segna lette
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulk("unread")} data-testid="bulk-unread">
              <Envelope size={14} className="mr-2" /> Segna non lette
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulk("delete")}
              className="text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-delete">
              <Trash size={14} className="mr-2" /> Elimina
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="kpi-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Nessuna notifica corrispondente ai filtri.</div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 py-2 border-b bg-[color:hsl(var(--surface-alt))] text-xs uppercase tracking-wider text-muted-foreground">
              <Checkbox
                checked={selected.size === items.length && items.length > 0}
                onCheckedChange={toggleAll}
                data-testid="check-all"
              />
              <div className="w-28">Tipo</div>
              <div className="flex-1">Contenuto</div>
              <div className="w-24 text-right">Quando</div>
              <div className="w-6" />
            </div>
            <div className="divide-y">
              {items.map(n => {
                const isSel = selected.has(n._id);
                const validUrl = n.target_url && n.target_url !== "/" && n.target_url.startsWith("/");
                return (
                  <div
                    key={n._id}
                    className={`flex items-start gap-3 px-4 py-3 transition-colors ${!n.is_read ? "bg-purple-50/50" : ""} ${isSel ? "bg-purple-100/70" : "hover:bg-slate-50"}`}
                    data-testid={`row-notif-${n._id}`}
                  >
                    <Checkbox
                      checked={isSel}
                      onCheckedChange={() => toggleSelect(n._id)}
                      className="mt-1"
                      data-testid={`check-${n._id}`}
                    />
                    <div className="w-28 text-xs">
                      <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                        {typeLabel(n.type)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRowClick(n)}
                      className="flex-1 min-w-0 text-left"
                      data-testid={`open-${n._id}`}
                    >
                      <div className="flex items-center gap-2">
                        {!n.is_read && <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                        <div className={`text-sm truncate ${!n.is_read ? "font-semibold" : ""}`}>{n.title}</div>
                      </div>
                      {n.body && <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{n.body}</div>}
                    </button>
                    <div className="w-24 text-right text-xs text-muted-foreground whitespace-nowrap" title={new Date(n.created_at).toLocaleString("it-IT")}>
                      {relTime(n.created_at)}
                    </div>
                    <div className="w-6 flex items-center justify-center">
                      {validUrl && <ArrowRight size={14} className="text-muted-foreground" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
